import {
    Box,
    Button,
    Chip,
    FormControl,
    FormControlLabel,
    Grid,
    Paper,
    Radio,
    RadioGroup,
    Stack,
    Typography,
} from '@mui/material';
import { Link as LinkIcon } from '@mui/icons-material';
import { ReactComponent as MingcuteCloseFillIcon } from '../../static/images/svg/mingcute_close-fill.svg';
import { ReactComponent as IcRoundPlusIcon } from '../../static/images/svg/ic_round-plus.svg';
import _ from 'lodash';
import React, { Fragment } from 'react';
import GlobalNotification from '../../component/common/snackbar';
import { SettingPersistSwitch } from '../../component/switch/setting-persist-switch.component';
import BASE_CHECK_TYPE from '../../constants/regular.constant';
import i18n from '../../i18n';
import { apiManage_rbp } from '../../request';
import CommonInput from '../../component/form/common-input.component';
import './scss/self-order-common-setting.page.scss';
import { BusinessHour2, BusinessHourValue, FirstMenu, PostBusinessHour2Params, SecondMenu } from '../../models';
import AlertDialog from '../../component/dialog/alert-dialog.component';
import MultiSelectTagDialog, { type MultiSelectTagOption } from '../../component/dialog/multi-select-tag-dialog.component';
import { generateBusinessHour2 } from '../../constants/utils';
import { RbpCloudSubFunctionAuxiliaryModeContext } from './rbp-cloud-sub-function-auxiliary-mode-context';
import {
    SELF_ORDER_COL1_KEYS,
    SELF_ORDER_COL2_KEYS,
    SELF_ORDER_COL3_KEYS,
} from './self-order-common-setting-column-keys';
import { SelfOrderCommonSettingPageSkeleton } from './self-order-common-setting-skeleton.component';

/** 各业务 `key` 对应辅助说明的 i18n key（中文与 Figma node 1838:51547 导出一致） */
const SELF_ORDER_AUX_HINT_I18N_KEYS: Partial<Record<number, string>> = {
    3: 'self_order_setting_aux_hint_3',
    38: 'self_order_setting_aux_hint_38',
    2: 'self_order_setting_aux_hint_2',
    73: 'self_order_setting_aux_hint_73',
    167: 'self_order_setting_aux_hint_167',
    118: 'self_order_setting_aux_hint_118',
    29: 'self_order_setting_aux_hint_29',
    111: 'self_order_setting_aux_hint_111',
    [-2]: 'self_order_setting_aux_hint_neg2',
    [-3]: 'self_order_setting_aux_hint_neg3',
    9: 'self_order_setting_aux_hint_9',
    78: 'self_order_setting_aux_hint_78',
    103: 'self_order_setting_aux_hint_103',
    21: 'self_order_setting_aux_hint_21',
    22: 'self_order_setting_aux_hint_22',
    108: 'self_order_setting_aux_hint_108',
    95: 'self_order_setting_aux_hint_95',
    136: 'self_order_setting_aux_hint_136',
    105: 'self_order_setting_aux_hint_105',
    156: 'self_order_setting_aux_hint_156',
    1: 'self_order_setting_aux_hint_1',
    27: 'self_order_setting_aux_hint_27',
    42: 'self_order_setting_aux_hint_42',
    107: 'self_order_setting_aux_hint_107',
    39: 'self_order_setting_aux_hint_39',
    93: 'self_order_setting_aux_hint_93',
    158: 'self_order_setting_aux_hint_158',
    180: 'self_order_setting_aux_hint_180',
    109: 'self_order_setting_aux_hint_109',
    133: 'self_order_setting_aux_hint_133',
    129: 'self_order_setting_aux_hint_129',
    138: 'self_order_setting_aux_hint_138',
    143: 'self_order_setting_aux_hint_143',
    139: 'self_order_setting_aux_hint_139',
    140: 'self_order_setting_aux_hint_140',
    86: 'self_order_setting_aux_hint_86',
};
export type BasicSettingType = {
    /** 当前值，随控件类型变化 */
    value: unknown;
    /** 展示标签 */
    label: string;
    /** 可选：标签宽度 */
    labelWidth?: number;
    /** 业务设置 id，负数表示派生 UI 字段 */
    key: number;
    /** 控件类型 */
    type: 'switch' | 'input' | 'radio' | 'select' | 'checkbox' | 'autoCompleteSelect';
    /** 数字或文本输入 */
    inputType?: 'number' | 'text';
    /** 单选项 */
    radioItems?: Array<{ label: string; value: string | number }>;
    /** 下拉选项 */
    options?: Array<{ label: string; value: string | number }>;
    /** 是否禁用 */
    disabled?: boolean;
};

/**
 * 云 RBP「子功能管理」-「自助点餐通用设置」：逻辑平迁自 RBP2，请求走 `apiManage_rbp`。
 * 隧道校验在 `rbp-cloud-sub-function.page` 的 `HookDecorator` 中统一完成。
 */
export default class SelfOrderCommonSettingPage extends React.Component<Props, State> {
    /** 读取顶栏「辅助模式」开关，用于展示 Figma 说明文案 */
    static contextType = RbpCloudSubFunctionAuxiliaryModeContext;
    /** 由 `contextType` 注入；不用 `declare` 字段，避免 Babel + legacy 装饰器编译报错 */
    context!: React.ContextType<typeof RbpCloudSubFunctionAuxiliaryModeContext>;

    // 是否能开启谷歌评价
    public isOpenGoogleReview = false;
    // 是否有POS收银端授权
    public isPosAuthorized = false;

    public state: State = {
        // 基础设置数组
        settingArr: [
            {
                value: '0',
                label: i18n.t('self_order_setting_page_menu_price_compatibility'),
                key: 78,
                type: 'radio',
                radioItems: [
                    { value: '1', label: i18n.t('self_order_setting_page_complex_menu_price') },
                    { value: '0', label: i18n.t('self_order_setting_page_normal_menu_price') },
                ]
            },
            {
                value: 0,
                label: i18n.t('self_order_setting_page_menu_view_ordering_rule'),
                key: 103,
                type: 'radio',
                radioItems: [
                    { value: 0, label: i18n.t('menu_and_dishes_page_menu_sort_value_2') },
                    { value: 1, label: i18n.t('menu_and_dishes_page_menu_sort_value_1') },
                ]
            },
            {
                value: 1,
                label: i18n.t('self_order_setting_page_dish_view'),
                key: 21,
                type: 'radio',
                radioItems: [
                    { value: 1, label: i18n.t('self_order_setting_page_dish_name_radio_local') },
                    { value: 0, label: i18n.t('self_order_setting_page_dish_name_radio_alt') },
                ]
            },
            {
                value: 'item_id ASC',
                label: i18n.t('self_order_setting_page_dish_view_ordering_rule'),
                key: 22,
                type: 'radio',
                radioItems: [
                    { value: 'item_name1 ASC', label: i18n.t('base_setting_dish_name') },
                    { value: 'item_id ASC', label: i18n.t('base_setting_dish_id') },
                    { value: 'slu_priority DESC', label: i18n.t('menu_and_dishes_page_dish_sort_slu_priority') },
                ]
            },
            {
                value: 0,
                label: i18n.t('self_order_setting_page_cop_default_order_view'),
                key: 42,
                type: 'radio',
                radioItems: [
                    { value: 0, label: i18n.t('self_order_setting_page_revolver_mode_type') },
                    { value: 1, label: i18n.t('self_order_setting_page_list_mode') }
                ]
            },
            {
                value: 0,
                label: i18n.t('self_order_setting_page_qop_default_order_view'),
                key: 107,
                type: 'radio',
                radioItems: [
                    { value: 0, label: i18n.t('self_order_setting_page_sidebar_mode') },
                    { value: 1, label: i18n.t('self_order_setting_page_list_mode') }
                ]
            },
            {
                value: 1,
                label: i18n.t('self_order_setting_page_cop_table_open_mode'),
                key: 39,
                type: 'radio',
                radioItems: [
                    { value: 0, label: i18n.t('self_order_setting_page_default_mode') },
                    { value: 1, label: i18n.t('self_order_setting_page_self_service_mode') }
                ]
            },
            {
                value: 1,
                label: i18n.t('self_order_setting_page_qop_table_open_mode'),
                key: 93,
                type: 'radio',
                radioItems: [
                    { value: 0, label: i18n.t('self_order_setting_page_default_mode') },
                    { value: 1, label: i18n.t('self_order_setting_page_self_service_mode') }
                ]
            },
            {
                value: 1,
                label: i18n.t('self_order_setting_page_customer_first_screen'),
                key: 27,
                type: 'radio',
                radioItems: [
                    { value: 1, label: i18n.t('self_order_setting_page_order_page') },
                    { value: 2, label: i18n.t('self_order_setting_page_notification_page') }
                ]
            },
            // todo 国际化
            {
                value: 1,
                label: i18n.t('self_order_setting_page_cop_qop_theme'),
                key: 158,
                type: 'radio',
                radioItems: [
                    { value: 1, label: i18n.t('self_order_setting_page_default_theme') },
                    { value: 2, label: i18n.t('self_order_setting_page_light_theme') },
                    { value: 3, label: i18n.t('self_order_setting_page_darkGold_theme') }
                ]
            },
            {
                value: 0,
                label: i18n.t('self_order_setting_page_cop_after_checkout_return'),
                key: 180,
                type: 'radio',
                radioItems: [
                    { value: 0, label: i18n.t('self_order_setting_page_after_checkout_open_table') },
                    { value: 1, label: i18n.t('self_order_setting_page_after_checkout_login_page') }
                ]
            },
            {
                value: 1,
                label: i18n.t('self_order_setting_page_single_dish_limit'),
                key: 3,
                type: 'switch'
            },
            // {
            //     value: 0,
            //     label: i18n.t('self_order_setting_page_self_ordering_function'),
            //     key: 14,
            //     type: 'switch'
            // },
            {
                value: 0,
                label: i18n.t('self_order_setting_page_open_guide_mode'),
                key: 86,
                type: 'switch'
            },
            {
                value: 0,
                label: i18n.t('self_order_setting_page_create_order_request'),
                key: 38,
                type: 'switch'
            },
            {
                value: 1,
                label: i18n.t('self_order_setting_page_round_order_rule'),
                key: 2,
                type: 'switch'
            },
            {
                value: 0,
                label: i18n.t('self_order_setting_page_one_sending_per_round'),
                key: 73,
                type: 'switch'
            },
            {
                value: 0,
                label: i18n.t('self_order_setting_page_the_ordered_dish_status_is_displayed'),
                key: 95,
                type: 'switch'
            },
            {
                value: 0,
                label: i18n.t('self_order_setting_page_dish_remark_show'),
                key: 105,
                type: 'switch'
            },
            {
                value: 1,
                label: i18n.t('self_order_setting_page_custom_remark'),
                key: 156,
                type: 'switch'
            },
            {
                value: 1,
                label: i18n.t('self_order_setting_page_is_show_sell_out_dishes'),
                key: 108,
                type: 'switch'
            },
            {
                value: 1,
                label: i18n.t('self_order_setting_page_call_service_function'),
                key: 109,
                type: 'switch'
            },
            {
                value: 0,
                label: i18n.t('self_order_setting_page_check_repeat_order'),
                key: 118,
                type: 'switch'
            },
            {
                value: 1,
                label: i18n.t('self_order_setting_page_show_menmber_login'),
                key: 129,
                type: 'switch'
            },
            {
                value: 0,
                label: i18n.t('self_order_setting_page_static_image_dynamic_code'),
                key: 133,
                type: 'switch'
            },
            {
                value: 0,
                label: i18n.t('self_order_setting_page_show_order_time'),
                key: 136,
                type: 'switch'
            },
            {
                value: 0,
                label: i18n.t('cop_setting_page_active_advertising'),
                key: 138,
                type: 'switch'
            },
            {
                value: 0,
                label: i18n.t('cop_setting_page_dinner_comment'),
                key: 139,
                type: 'switch'
            },
            {
                value: 0,
                label: i18n.t('cop_setting_page_google_comment'),
                key: 140,
                type: 'switch'
            },
            {
                value: 0,
                label: i18n.t('self_order_setting_page_ignore_order_check'),
                key: 111,
                type: 'switch'
            },
            {
                value: '',
                label: '',
                key: -111,
                type: 'input',
            },
            {
                value: '',
                label: '',
                key: -111.1,
                type: 'input',
            },
            {
                value: 0,
                label: i18n.t('self_order_setting_page_preorder_next_round'),
                key: 167,
                type: 'switch'
            },
            {
                value: 10,
                label: i18n.t('self_order_setting_page_one_sending_per_round_time_min_default'),
                key: -2,
                type: 'input',
                inputType: 'number'
            },
            {
                value: 10,
                label: i18n.t('self_order_setting_page_each_person_per_round_oder_count_default'),
                key: -3,
                type: 'input',
                inputType: 'number'
            },
            {
                value: 9999,
                label: i18n.t('self_order_setting_page_rule_global_turn_max_number_default'),
                key: 9,
                type: 'input',
                inputType: 'number'
            },
            {
                value: 15,
                label: i18n.t('self_order_setting_page_notification_time_before_closing'),
                key: 29,
                type: 'input',
                inputType: 'number'
            },
            {
                value: 5,
                label: i18n.t('cop_setting_page_show_advertising_time'),
                key: 143,
                type: 'select',
                options: [
                    { value: 5, label: '5' },
                    { value: 10, label: '10' },
                    { value: 15, label: '15' },
                ],
            },
            {
                value: [],
                label: i18n.t('self_order_setting_page_mop_qop_menu'),
                key: 1,
                type: 'autoCompleteSelect',
                options: [],
            },
            // 若往下添加，需改下面的settingArr.slice(settingArr.length - x)
        ],
        backupSettingArr: [],
        /** 各设置项 key 是否在请求中（用于开关 loading、输入禁用等） */
        persistingKeys: {} as Partial<Record<number, boolean>>,
        requestQueue: {
            // 获取/修改每人每轮点菜数和点餐时间
            2: {
                field: 'enable',
                rejected: () => GlobalNotification.error(i18n.t('self_order_setting_page_get_round_rule_faile')),
                request: async () => await apiManage_rbp.get_setting(2),
                patchRequest: async (param: {
                    enable: boolean;
                    turn_mins: string;
                    turn_dishes: string;
                }) => await apiManage_rbp.patch_setting(2, {
                    init: true,
                    enable: param.enable,
                    turn_mins: param.turn_mins,
                    turn_dishes: param.turn_dishes,
                })
            },
            // 获取/修改显示的菜品名
            21: {
                field: 'showItemName1',
                rejected: () => GlobalNotification.error(i18n.t('self_order_setting_page_get_dish_name_view_failed')),
                request: async () => await apiManage_rbp.get_setting(21),
                patchRequest: async (isShowDishName1: boolean) => await apiManage_rbp.patch_dish_display_name(isShowDishName1)
            },
            // 获取/修改显示的菜品排序
            22: {
                rejected: () => GlobalNotification.error(i18n.t('self_order_setting_page_get_dish_ordering_rule_failed')),
                request: async () => await apiManage_rbp.get_setting(22),
                patchRequest: async (value: string) => await apiManage_rbp.patch_dish_sort(value)
            },
            // 获取/修改开台引导模式
            86: {
                rejected: () => GlobalNotification.error(i18n.t('self_order_setting_page_get_table_open_guide_mode_failed')),
                request: async () => await apiManage_rbp.get_setting(86),
                patchRequest: async (param: { id: number; value: string; }) =>
                    await apiManage_rbp.patchSettingById(param.id, { stringValue: param.value })
            },
            // 获取/修改是否开启自主点餐功能
            14: {
                field: 'status',
                rejected: () => GlobalNotification.error(i18n.t('self_order_setting_page_get_whether_on_self_help_order_function_failed')),
                request: async () => await apiManage_rbp.get_setting(14),
                patchRequest: async (isOn: boolean) => await apiManage_rbp.patch_setting(14, { status: isOn })
            },
            // 获取/修改点餐视图模式
            42: {
                rejected: () => GlobalNotification.error(i18n.t('self_order_setting_page_get_order_view_mode_failed')),
                request: async () => await apiManage_rbp.get_setting(42),
                patchRequest: async (param: { id: number; value: string; }) =>
                    await apiManage_rbp.patchSettingById(param.id, { stringValue: param.value })
            },
            // 获取/修改COP开台模式
            39: {
                rejected: () => GlobalNotification.error(i18n.t('self_order_setting_page_get_cop_table_open_mode_failed')),
                request: async () => await apiManage_rbp.get_setting(39),
                patchRequest: async (param: { id: number; value: string; }) =>
                    await apiManage_rbp.patchSettingById(param.id, { stringValue: param.value })
            },
            // 获取/修改QOP开台模式
            93: {
                rejected: () => GlobalNotification.error(i18n.t('self_order_setting_page_get_qop_table_open_mode_failed')),
                request: async () => await apiManage_rbp.get_setting(93),
                patchRequest: async (param: { id: number; value: string; }) =>
                    await apiManage_rbp.patchSettingById(param.id, { stringValue: param.value })
            },
            // 获取已下单菜品状态  开关状态  是否显示已下单菜品状态
            95: {
                rejected: () => GlobalNotification.error(i18n.t('self_order_setting_page_failed_to_get_displayed_ordered_food_status')),
                request: async () => await apiManage_rbp.get_setting(95),
                patchRequest: async (param: { id: number; value: string; }) =>
                    await apiManage_rbp.patchSettingById(param.id, { stringValue: param.value })
            },
            // 获取/修改单品限制
            3: {
                field: 'enable',
                rejected: () => GlobalNotification.error(i18n.t('self_order_setting_page_get_single_limit_failed')),
                request: async () => await apiManage_rbp.get_setting(3),
                patchRequest: async (isOn: boolean) => await apiManage_rbp.post_update_shark_settings({
                    id: 3,
                    set: { init: true, enable: isOn },
                    rule_lnr_enable: Number(isOn)
                })
            },
            // 获取/修改是否开启一轮一次发送
            73: {
                rejected: () => GlobalNotification.error(i18n.t('self_order_setting_page_get_whether_on_round_one_send_failed')),
                request: async () => await apiManage_rbp.get_setting(73),
                patchRequest: async (onOrOff: string) =>
                    await apiManage_rbp.patchRestOneTimeOneSend({ on_off: onOrOff })
            },
            // 获取/修改最大点餐回合数
            9: {
                rejected: () => GlobalNotification.error(i18n.t('self_order_setting_page_get_max_order_round_amount_failed')),
                request: async () => await apiManage_rbp.get_setting(9),
                patchRequest: async (param: { id: number; value: string; }) =>
                    await apiManage_rbp.patchSettingById(param.id, { stringValue: param.value })
            },
            // 获取/修改开台审核
            38: {
                field: 'enable',
                rejected: () => GlobalNotification.error(i18n.t('self_order_setting_page_get_table_order_audit_failed')),
                request: async () => await apiManage_rbp.get_setting(38),
                patchRequest: async (isOn: boolean) => await apiManage_rbp.patch_setting(38, { enable: isOn })
            },
            27: {
                rejected: () => GlobalNotification.error(i18n.t('self_order_setting_page_get_customer_main_page_failed')),
                request: async () => await apiManage_rbp.get_setting(27),
                patchRequest: async (param: { id: number; value: string; }) =>
                    await apiManage_rbp.patchSettingById(param.id, { stringValue: param.value })
            },
            158: {
                rejected: () => GlobalNotification.error(i18n.t('self_order_setting_page_get_cop_theme_failed')),
                request: async () => await apiManage_rbp.get_setting(158),
                patchRequest: async (param: { id: number; value: string; }) =>
                    await apiManage_rbp.patchSettingById(param.id, { stringValue: param.value })
            },
            180: {
                rejected: () => GlobalNotification.error(i18n.t('self_order_setting_page_get_after_checkout_return_failed')),
                request: async () => await apiManage_rbp.get_setting(180),
                patchRequest: async (param: { id: number; value: string; }) =>
                    await apiManage_rbp.patchSettingById(param.id, { stringValue: param.value })
            },
            29: {
                field: 'mins',
                rejected: () => GlobalNotification.error(i18n.t('self_order_setting_page_get_notify_time_before_closing_time_failed')),
                request: async () => await apiManage_rbp.get_setting(29),
                patchRequest: async (time: string) =>
                    await apiManage_rbp.patch_setting(29, { mins: Number(time) })
            },
            // 获取菜单价格兼容性
            78: {
                rejected: () => GlobalNotification.error(i18n.t('self_order_setting_page_get_menu_price_compatibility_failed')),
                request: async () => await apiManage_rbp.get_menu_item_main(),
                patchRequest: async (val: string) => await apiManage_rbp.patch_menu_item_main(val),
            },
            // 菜单排序
            103: {
                rejected: () => GlobalNotification.error(i18n.t('self_order_setting_page_get_menu_sort_rule_failed')),
                request: async () => await apiManage_rbp.get_setting(103),
                patchRequest: async (val: string) => await apiManage_rbp.patchSettingById(103, { stringValue: val }),
            },
            // 自助菜品备注
            105: {
                rejected: () => GlobalNotification.error(i18n.t('self_order_setting_page_get_dish_remark_show_failed')),
                request: async () => await apiManage_rbp.get_setting(105),
                patchRequest: async (val: string) => await apiManage_rbp.patchSettingById(105, { stringValue: val == '0' ? 1 : 0 }),
            },
            // 自定义菜品备注
            156: {
                rejected: () => GlobalNotification.error(i18n.t('self_order_setting_page_get_custom_remark_failed')),
                request: async () => await apiManage_rbp.get_setting(156),
                patchRequest: async (val: string) => await apiManage_rbp.patchSettingById(156, { stringValue: val }),
            },
            // qop视图
            107: {
                rejected: () => GlobalNotification.error(i18n.t('self_order_setting_page_get_order_view_mode_failed')),
                request: async () => await apiManage_rbp.get_setting(107),
                patchRequest: async (param: { id: number; value: string; }) =>
                    await apiManage_rbp.patchSettingById(param.id, { stringValue: param.value })
            },
            // 显示沽清菜品
            108: {
                rejected: () => GlobalNotification.error(i18n.t('self_order_setting_page_get_is_show_sell_out_dishes_failed')),
                request: async () => await apiManage_rbp.get_setting(108),
                patchRequest: async (val: string) => await apiManage_rbp.patchSettingById(108, { stringValue: val }),
            },
            // 呼叫服务功能
            109: {
                rejected: () => GlobalNotification.error(i18n.t('self_order_setting_page_get_call_service_failed')),
                request: async () => await apiManage_rbp.get_setting(109),
                patchRequest: async (val: string) => await apiManage_rbp.patchSettingById(109, { stringValue: val }),
            },
            // 忽略订单完成度检查
            111: {
                field: 'enable',
                rejected: () => GlobalNotification.error(i18n.t('self_order_setting_page_get_ignore_order_check_failed')),
                request: async () => await apiManage_rbp.get_setting(111),
                patchRequest: async (param: {
                    enable: boolean;
                    ignore_menus: string;
                    ignore_print_class: string;
                }) =>
                    await apiManage_rbp.patch_setting(111, {
                        enable: param.enable,
                        ignore_menus: JSON.parse(param.ignore_menus),
                        ignore_print_class: JSON.parse(param.ignore_print_class),
                    })
            },
            // 重复下单检查
            118: {
                rejected: () => GlobalNotification.error(i18n.t('self_order_setting_page_get_check_repeat_order_error')),
                request: async () => await apiManage_rbp.get_setting(118),
                patchRequest: async (val: string) => await apiManage_rbp.patchSettingById(118, { stringValue: val }),
            },
            // MOP、QOP打包专属菜单
            1: {
                rejected: () => GlobalNotification.error(i18n.t('self_order_setting_page_get_mop_qop_menu_failed')),
                request: async () => {
                    const res = await apiManage_rbp.getMopSelectMenu();
                    if (_.get(res, 'error')) return { error: true } as any;
                    return [{
                        id: 1,
                        name: '',
                        value: res
                    }]
                },
                patchRequest: async (val: number[]) => await apiManage_rbp.patchMopSelectMenu(val),
            },
            // 显示会员登录页面
            129: {
                rejected: () => GlobalNotification.error(i18n.t('self_order_setting_page_show_menmber_login_error')),
                request: async () => await apiManage_rbp.get_setting(129),
                patchRequest: async (val: string) => await apiManage_rbp.patchSettingById(129, { stringValue: val }),
            },
            // 开启图片动态刷新
            133: {
                rejected: () => GlobalNotification.error(i18n.t('self_order_setting_page_get_static_image_dynamic_code_failed')),
                request: async () => await apiManage_rbp.get_setting(133),
                patchRequest: async (val: string) => await apiManage_rbp.patchSettingById(133, { stringValue: val }),
            },
            // 显示菜品下单时间
            136: {
                rejected: () => GlobalNotification.error(i18n.t('self_order_setting_page_get_show_order_time_failed')),
                request: async () => await apiManage_rbp.get_setting(136),
                patchRequest: async (val: string) => await apiManage_rbp.patchSettingById(136, { stringValue: val }),
            },
            // 显示QOP二维码
            // 137: {
            //     rejected: () => GlobalNotification.error(i18n.t('')),
            //     request: async () => await apiManage_rbp.get_setting(137),
            //     patchRequest: async (val: string) => await apiManage_rbp.patchSettingById(137, {stringValue: val}),
            // }
            // 活动广告	
            138: {
                rejected: () => GlobalNotification.error(i18n.t('self_order_setting_page_get_setting_failed', { setting: i18n.t('cop_setting_page_active_advertising')})),
                request: async () => await apiManage_rbp.get_setting(138),
                patchRequest: async (val: string) => await apiManage_rbp.patchSettingById(138, { stringValue: val }),
            },
            // 用餐评价	
            139: {
                rejected: () => GlobalNotification.error(i18n.t('self_order_setting_page_get_setting_failed', { setting: i18n.t('cop_setting_page_dinner_comment')})),
                request: async () => await apiManage_rbp.get_setting(139),
                patchRequest: async (val: string) => await apiManage_rbp.patchSettingById(139, { stringValue: val }),
            },
            // 谷歌评价	
            140: {
                rejected: () => GlobalNotification.error(i18n.t('self_order_setting_page_get_setting_failed', { setting: i18n.t('cop_setting_page_google_comment')})),
                request: async () => await apiManage_rbp.get_setting(140),
                patchRequest: async (val: string) => await apiManage_rbp.patchSettingById(140, { stringValue: val }),
            },
            // 无操作显示广告的时间		
            143: {
                rejected: () => GlobalNotification.error(i18n.t('self_order_setting_page_get_setting_failed', { setting: i18n.t('cop_setting_page_show_advertising_time')})),
                request: async () => await apiManage_rbp.get_setting(143),
                patchRequest: async (val: string) => await apiManage_rbp.patchSettingById(143, { stringValue: val }),
            },
            // 预定下一轮菜品
            167: {
                rejected: () => GlobalNotification.error(i18n.t('self_order_setting_page_get_setting_failed', { setting: i18n.t('self_order_setting_page_preorder_next_round')})),
                request: async () => await apiManage_rbp.get_setting(167),
                patchRequest: async (val: string) => await apiManage_rbp.patchSettingById(167, { stringValue: val }),
            },
        },
        roundRule: {} as RoundRuleParam,
        ignoreOrder: {} as IgnoreOrderParam,
        menusList: [],
        isAlertOpen: false,
        /** 「同步轮次设置到营业时间」确认弹窗：与 AlertDialog.confirmLoading 联动，展示公共 LoadingOverlayButton */
        syncRoundToBusinessHourConfirmLoading: false,
        firstMenus: [],
        printGroup: [],
        /** 多选弹窗：忽略菜单/忽略岗位/专属菜单 仅允许同时打开其一 */
        tagPickerKind: null,
        tagPickerDraftIds: [],
        /** 首包：init + 二级菜单/打印组/谷歌评价依赖；完成前展示三列骨架，避免整页仅顶条 loading */
        initialPageDataReady: false,
    }

    // *********************
    // Life Cycle Function
    // *********************
    // 须为原型方法（非类字段箭头函数），与历史隧道装饰器兼容；隧道校验现由子功能壳页 `HookDecorator` 统一处理。

    public async componentDidMount(): Promise<void> {
        try {
            await this.init();
            await this.getSecondMenus();
            await this.getPrintClass();
            await this.canOpenGoogleReview();
        } finally {
            /** 与「我的餐厅」等页一致：成功或失败均结束首屏骨架，避免卡在占位 */
            this.setState({ initialPageDataReady: true });
        }
    }

    // *********************
    // Business Function
    // *********************

    init = async () => {
        const { requestQueue, settingArr } = this.state;
        const newSettingArr = _.cloneDeep(settingArr);
        let roundRule = {} as RoundRuleParam;
        let ignoreOrder = {} as IgnoreOrderParam;
        const result = await Promise.all(Object.values(requestQueue).map(item => item.request?.()));
        result.forEach((item, index) => {
            if (_.get(item, 'error')) {
                Object.values(requestQueue)[index]?.rejected();
            } else {
                const id = _.get(item, ['0', 'id']);
                const value = _.get(item, ['0', 'value']);
                id === 2 && (roundRule = value);
                id === 111 && (ignoreOrder = value);
                if (id === 111) {
                    ignoreOrder = value;
                    const ignore_menus = _.isNumber(ignoreOrder.ignore_menus) ? [ignoreOrder.ignore_menus] : ignoreOrder.ignore_menus || [];
                    const ignore_print_class = _.isNumber(ignoreOrder.ignore_print_class) ? [ignoreOrder.ignore_print_class] : ignoreOrder.ignore_print_class || [];
                    ignoreOrder.ignore_menus = JSON.stringify(ignore_menus);
                    ignoreOrder.ignore_print_class = JSON.stringify(ignore_print_class);
                }
                const field = requestQueue?.[id]?.field;
                newSettingArr.forEach(ele => {
                    const formatValue = field ? Number((value?.[field])) : value;
                    id === ele?.key && _.set(ele, 'value', formatValue);
                    ele?.key === -2 && _.set(ele, 'value', roundRule?.turn_mins || '0');
                    ele?.key === -3 && _.set(ele, 'value', roundRule?.turn_dishes || '0');
                    id === 105 && ele?.key === 105 && _.set(ele, 'value', formatValue === 1 ? 0 : 1);
                    ele?.key === -111 && _.set(ele, 'value', ignoreOrder?.ignore_menus);
                    ele?.key === -111.1 && _.set(ele, 'value', ignoreOrder?.ignore_print_class);
                    if (ele?.key === 1 && id === 1) {
                        const packetSelectMenu: number[] = [];
                        formatValue?.forEach((m: any) => {
                            m?.is_selected && packetSelectMenu.push(m?.main_group_id);
                        })
                        _.set(ele, 'value', packetSelectMenu);
                    }
                });
            }
        })
        this.setState({
            settingArr: newSettingArr,
            backupSettingArr: newSettingArr,
            roundRule,
            ignoreOrder
        });
    }

    /**
     * 更新本地表单状态（轮次、忽略订单等与派生 state 同步）。
     * @returns 是否已应用变更；谷歌评价等校验失败时为 false，调用方不应再发起保存请求。
     */
    onChangeSetting = (value: unknown, key: number): boolean => {
        if (key === 140) {
            if (!this.isOpenGoogleReview) {
                GlobalNotification.error(i18n.t('cop_setting_page_google_address_config_error'));
                return false;
            }
            if (!this.isPosAuthorized) {
                GlobalNotification.error(`${i18n.t('expand_page_no_authorization_or_expired')}: ${i18n.t(`expand_page_AU1003`)}`);
                return false;
            }
        }
        const newSettingArr = _.cloneDeep(this.state.settingArr);
        const newRoundRule = _.cloneDeep(this.state.roundRule);
        const newIgnoreOrder = _.cloneDeep(this.state.ignoreOrder);
        for (let i = 0, len = newSettingArr.length; i < len; i++) {
            if (newSettingArr[i]?.key === key) {
                key === -2 && _.set(newRoundRule, 'turn_mins', Number(value).toFixed(0));
                key === -3 && _.set(newRoundRule, 'turn_dishes', Number(value).toFixed(0));
                key === 2 && _.set(newRoundRule, 'enable', value);
                key === 111 && _.set(newIgnoreOrder, 'enable', value);
                key === -111 && _.set(newIgnoreOrder, 'ignore_menus', value);
                key === -111.1 && _.set(newIgnoreOrder, 'ignore_print_class', value);
                if (key === -2 || key === -3 || key === 9 || key === 29) {
                    newSettingArr[i].value = Number(Number(value).toFixed(0))
                } else {
                    newSettingArr[i].value = value;
                }
                break;
            }
        }
        this.setState({
            settingArr: newSettingArr,
            roundRule: newRoundRule,
            ignoreOrder: newIgnoreOrder
        });
        return true;
    }

    /**
     * 根据设置项 key 解析对应的 patch 请求函数（轮次合并为 id=2，忽略订单合并为 id=111）。
     */
    resolvePatchRequest = (uiKey: number): ((param: unknown) => Promise<unknown>) | null => {
        const q = this.state.requestQueue;
        if ([-2, -3, 2].includes(uiKey)) {
            return (q[2]?.patchRequest as (param: unknown) => Promise<unknown>) ?? null;
        }
        if ([-111, -111.1, 111].includes(uiKey)) {
            return (q[111]?.patchRequest as (param: unknown) => Promise<unknown>) ?? null;
        }
        return (q[uiKey]?.patchRequest as (param: unknown) => Promise<unknown>) ?? null;
    }

    /** 与 {@link persistSettingByUiKey} 共用的 loading state key（忽略订单多字段共用 111） */
    resolvePersistLoadingKey = (uiKey: number): number =>
        ([-111, -111.1, 111].includes(uiKey) ? 111 : uiKey);

    /**
     * 接口失败时把表单与派生 state 恢复为上次持久化成功的快照。
     */
    restoreFromBackup = (): void => {
        const backup = _.cloneDeep(this.state.backupSettingArr);
        const rItem = backup.find((i) => i.key === 2);
        const tm = backup.find((i) => i.key === -2);
        const td = backup.find((i) => i.key === -3);
        const i111 = backup.find((i) => i.key === 111);
        const iMenus = backup.find((i) => i.key === -111);
        const iPrint = backup.find((i) => i.key === -111.1);
        this.setState({
            settingArr: backup,
            roundRule: {
                ...this.state.roundRule,
                enable: Boolean(rItem?.value),
                turn_mins: String(tm?.value ?? 0),
                turn_dishes: String(td?.value ?? 0),
            },
            ignoreOrder: {
                ...this.state.ignoreOrder,
                enable: Boolean(i111?.value),
                ignore_menus: String(iMenus?.value ?? '[]'),
                ignore_print_class: String(iPrint?.value ?? '[]'),
            },
        });
    }

    /**
     * 将当前项持久化到后端（单字段）；成功则刷新 backup，失败则回滚并提示。
     * @param uiKey - {@link BasicSettingType.key}
     * @returns 是否保存成功（用于多选弹窗在成功后再关窗）
     */
    persistSettingByUiKey = async (uiKey: number): Promise<boolean> => {
        const loadingKey = this.resolvePersistLoadingKey(uiKey);
        if (this.state.persistingKeys[loadingKey]) {
            return false;
        }
        const patchRequest = this.resolvePatchRequest(uiKey);
        if (!patchRequest) {
            return false;
        }
        const single: Record<string, Request> = {
            [String(uiKey)]: {
                key: uiKey,
                value: null as unknown,
                patchRequest: patchRequest as Request['patchRequest'],
            },
        };
        if (!this.regularCheck(single)) {
            return false;
        }
        this.setState((prev) => ({
            persistingKeys: { ...prev.persistingKeys, [loadingKey]: true },
        }));
        let success = false;
        try {
            const currentRow = this.state.settingArr.find((x) => x.key === uiKey);
            const param = this.formatParameter({
                key: uiKey,
                value: currentRow !== undefined ? currentRow.value : null,
                patchRequest: patchRequest as Request['patchRequest'],
            });
            const res = await (patchRequest as Request['patchRequest'])(param);
            if (_.get(res, 'error')) {
                GlobalNotification.error(i18n.t('global_text_update_failure'));
                this.restoreFromBackup();
            } else {
                GlobalNotification.success(i18n.t('global_text_update_success'));
                this.setState({
                    backupSettingArr: _.cloneDeep(this.state.settingArr),
                });
                success = true;
            }
        } catch {
            GlobalNotification.error(i18n.t('global_text_update_failure'));
            this.restoreFromBackup();
        } finally {
            this.setState((prev) => ({
                persistingKeys: { ...prev.persistingKeys, [loadingKey]: false },
            }));
        }
        return success;
    }

    /**
     * 输入框失焦时：若相对上次成功保存有变更则调接口持久化。
     * @param key - {@link BasicSettingType.key}
     */
    persistInputFieldIfDirty = (key: number): void => {
        const cur = this.state.settingArr.find((x) => x.key === key);
        const bak = this.state.backupSettingArr.find((x) => x.key === key);
        if (!cur || !bak || cur.value === bak.value) {
            return;
        }
        void this.persistSettingByUiKey(key);
    }

    /** 在 onChangeSetting 已入队后调度单次持久化（等待 state 合并后再读最新值） */
    schedulePersistAfterChange = (uiKey: number): void => {
        this.setState({}, () => {
            void this.persistSettingByUiKey(uiKey);
        });
    }

    /**
     * 格式化修改接口传参
     * @param item
     */
    formatParameter = (item: Request) => {
        let value;
        const specialIdGroup: Record<string, unknown> = {
            21: Boolean(item?.value),
            73: `${Number(item?.value)}`,
            9: { id: item.key, value: Number(item.value).toFixed(0) },
            // 29: { id: item.key, value: Number(item.value).toFixed(0) },
            39: { id: item.key, value: String(item?.value) },
            93: { id: item.key, value: String(item?.value) },
            95: { id: item.key, value: String(item?.value) },
            42: { id: item.key, value: String(item?.value) },
            22: { id: item.key, value: item?.value },
            86: { id: item.key, value: String(Number(item?.value)) },
            27: { id: item.key, value: item?.value },
            158: { id: item.key, value: item?.value },
            180: { id: item.key, value: item?.value },
            105: `${Number(item?.value)}`,
            156: `${Number(item?.value)}`,
            107: { id: item.key, value: String(item?.value) },
            108: `${Number(item?.value)}`,
            109: `${Number(item?.value)}`,
            118: `${Number(item?.value)}`,
        }
        if ([-2, -3, 2].includes(item?.key)) {
            value = this.state.roundRule;
        } else if ([111, -111, -111.1].includes(item?.key)) {
            value = this.state.ignoreOrder;
        }
        else {
            value = specialIdGroup[item.key] ?? item.value;
        }
        return value;
    }

    /**
     * 检验修改的项
     * @param changedItem 修改的项
     * @returns boolean
     */
    regularCheck = (changedItem: Record<string, Request>): boolean => {
        let isCheked = true;
        const checkKeys = Object.keys(changedItem);
        const { settingArr } = this.state;
        const regular = BASE_CHECK_TYPE['roundRules']?.regular;
        if (checkKeys.includes('9') || checkKeys.includes('-2') || checkKeys.includes('-3')) {
            settingArr.forEach(item => {
                if ([9, -2, -3].includes(item?.key)) {
                    if (!regular.test(String(item?.value))) {
                        GlobalNotification.error(`${item?.label}-${i18n.t(BASE_CHECK_TYPE['roundRules']?.helperText)}`);
                        isCheked = false;
                    }
                }
            });
        }
        return isCheked;
    }

    /**
     * 获取全部二级菜单（忽略订单检查多选等）；菜单树请求使用 `apiManage_rbp.get_menus`（v2），与云迁移「v2 保持 v2、不在此页升格 v3」一致。
     */
    getSecondMenus = async () => {
        const result = await apiManage_rbp.get_menus_v3({})
        const result2 = await apiManage_rbp.get_menus_v3({ type: 0 })
        if (!_.get(result, 'error')) {
            const list: (SecondMenu & { first_menu_name?: string })[] = [];
            const firstMenus: FirstMenu[] = _.isArray(result2) ? result2 : [];
            result.forEach(item => {
                if (item?.menu_item_slu?.length) {
                    item.menu_item_slu.forEach(temp => {
                        list.push({
                            ...temp,
                            first_menu_name: item.main_group_name
                        })
                    })
                }
            })
            this.setState({
                menusList: list,
                firstMenus: firstMenus
            })
        } else {
            // 与 rwa/kwa 等页一致：卸载取消的请求不提示失败
            if (_.get(result, 'message') === 'canceled') {
                return;
            }
            GlobalNotification.error(`${i18n.t('kwa_setting_page_get_first_level_menu_failed')}`)
        }
    }

    setAlertOpen = (isOpen: boolean) => {
        if (isOpen) {
            this.setState({ isAlertOpen: true });
        } else {
            // 关窗时同步复位 loading，避免再次打开时仍显示菊花
            this.setState({ isAlertOpen: false, syncRoundToBusinessHourConfirmLoading: false });
        }
    }

    /**
     * 同步轮次设置到营业时间
     */
    asyncTurnSetting = async () => {
        this.setState({ syncRoundToBusinessHourConfirmLoading: true });
        try {
            const turn_dishes = Number(this.state.settingArr.find((item) => item.key === -3)?.value ?? 0);
            const turn_mins = Number(this.state.settingArr.find((item) => item.key === -2)?.value ?? 0);
            const max_turn = Number(this.state.settingArr.find((item) => item.key === 9)?.value ?? 0);
            // 获取复合型营业时间数据
            const businessHour2: BusinessHour2[] = await apiManage_rbp.get_business_hour2();
            // 没有数据时生成营业时间数据
            if (_.get(businessHour2, 'error') || businessHour2.length === 0) {
                for (let i = 0; i < 7; i++) {
                    const data = generateBusinessHour2(`${i}`, '0', turn_dishes, turn_mins, max_turn);
                    businessHour2.push(data)
                }
            }
            //把周日挪到后面，调整数据位置
            const item = businessHour2?.shift() || {};
            // 自定义日期在周日后面
            if (businessHour2.length > 6) {
                const removed = businessHour2.splice(6);
                businessHour2.push(item, ...removed);
            } else {
                businessHour2.push(item);
            }
            // 同步轮次设置
            businessHour2.forEach(item => {
                item.value?.forEach(temp => {
                    temp.turn_dishes = turn_dishes;
                    temp.turn_mins = turn_mins;
                    temp.max_turn = max_turn;
                })
                item?.value2?.forEach(temp => {
                    temp.turn_dishes = turn_dishes;
                    temp.turn_mins = turn_mins;
                    temp.max_turn = max_turn;
                })
            })
            // 转换成保存的格式
            const param = businessHour2.reduce((prev: PostBusinessHour2Params[], cur) => {
                // 24小时营业
                const value = (cur.value || []).map((item) => (this.transform(cur, item)));
                // 自主模式
                const value2 = (cur.value2 || []).map((item) => (this.transform(cur, item)));
                return _.concat(prev, value, value2)
            }, [])
            // 保存
            const result = await apiManage_rbp.post_business_hour2(param);
            if (!_.get(result, 'error')) {
                GlobalNotification.success(i18n.t('global_text_operate_success'))
                this.setState({
                    isAlertOpen: false
                })
            }
            else {
                GlobalNotification.error(i18n.t('global_text_operate_failure'))
            }
        } catch {
            GlobalNotification.error(i18n.t('global_text_operate_failure'))
        } finally {
            this.setState({ syncRoundToBusinessHourConfirmLoading: false });
        }
    }

    /**
     * 格式转换
     * @param businessHour
     * @param businessHourValue
     * @returns
     */
    transform(businessHour: BusinessHour2, businessHourValue: BusinessHourValue): PostBusinessHour2Params {
        const result: PostBusinessHour2Params = {
            //  营业时间的描述（0：营业；1：不营业；）
            value: businessHourValue.value || '0',
            // 数据模式（0?:24小时；1：自主模式）
            data_type: businessHourValue.data_type || '0',
            // 前端模式设置（0?:24小时；1：自主模式）
            pattern: businessHour.pattern || '0',
            // 营业时间的类型（0：星期 + 时间；1：日期 + 时间；）
            type: businessHour.type || '0',
            // 2022-03-24 日期必传 星期不用传
            date: businessHour.date,
            // '0-6' 0周日 6周六 星期必传 日期不用传
            week: businessHour.date,
            // 首选菜单
            preferred_menu: businessHourValue.preferred_menu || [],
            // 备选菜单
            alternative_menu: businessHourValue.alternative_menu || [],
            // 必选菜单
            default_menu: businessHourValue.default_menu || [],
            // 隐藏菜单
            hide_menu: businessHourValue.hide_menu || [],
            // 结束时间 "01:10"
            end_time: businessHourValue.data_type === '0' ? '24:00' : (businessHourValue.end_time || '00:00'),
            // 开始时间
            start_time: businessHourValue.data_type === '0' ? '00:00' : (businessHourValue.start_time || '00:00'),
            // 项目 "QOP"; "COP2"; "RWA2"; "POS"
            project: businessHourValue.project || [],
            // 暂时用不到 默认0
            // 是否允许自提 (0：自提；1：非自提)
            selftake_status: '0',
            // 是否允许外卖（0：外卖；1：非外卖）
            takeaway_status: '0',
            // 每轮点餐数量
            turn_dishes: Number(businessHourValue.turn_dishes || 0),
            // 每轮点餐时间
            turn_mins: Number(businessHourValue.turn_mins || 0),
            // 最大轮次
            max_turn: Number(businessHourValue.max_turn || 0),
        }
        return result
    }

    getOptions = (item: BasicSettingType) => {
        if (!item.options) return [];
        if (item.key === 1) {
            return this.state.firstMenus.map(temp => ({
                value: Number(temp.main_group_id),
                label: temp.main_group_name || '',
            }))
        }
        return item.options
    }

    /**
     * 获取打印组列表 （对应KWA岗位）
     */
    public async getPrintClass() {
        const result = await apiManage_rbp.getPrinterClass();
        this.setState({
            printGroup: _.isArray(result) ? result : []
        })
    }

    /**
     * 校验能否开启谷歌评价
     */
    canOpenGoogleReview = async () => {
        this.isOpenGoogleReview = false;
        this.isPosAuthorized = false;

        const result = await apiManage_rbp.getRestaurantAddress();
        const place_id = result?.place_id || ''
        this.isOpenGoogleReview = !!place_id
        if (!place_id) return;
        // 授权信息
        const data = await apiManage_rbp.getAuthorizationMessage();
        const authorizationMessage = _.get(data, 'warrant_data', '');
        let isHas = false;
        if (_.isArray(authorizationMessage) && authorizationMessage.length > 0) {
            isHas = !!authorizationMessage.find(item => item?.code === 'AU1003');
        }
        this.isPosAuthorized = isHas;
    }

    /** 打开多选标签弹窗并载入当前已选项到草稿 */
    private openTagPicker = (kind: 'ignoreMenu' | 'ignorePosition' | 'mopMenu') => {
        const draft = this.getDraftIdsForTagPicker(kind);
        this.setState({ tagPickerKind: kind, tagPickerDraftIds: draft });
    };

    /** 关闭多选弹窗（未写入表单） */
    private closeTagPicker = () => {
        this.setState({ tagPickerKind: null, tagPickerDraftIds: [] });
    };

    /** 从表单当前值解析弹窗草稿 id 列表 */
    private getDraftIdsForTagPicker = (kind: 'ignoreMenu' | 'ignorePosition' | 'mopMenu'): number[] => {
        const { settingArr } = this.state;
        if (kind === 'ignoreMenu') {
            const v = settingArr.find((x) => x.key === -111)?.value;
            return JSON.parse(String(v ?? '[]')) as number[];
        }
        if (kind === 'ignorePosition') {
            const v = settingArr.find((x) => x.key === -111.1)?.value;
            return JSON.parse(String(v ?? '[]')) as number[];
        }
        const v = settingArr.find((x) => x.key === 1)?.value;
        return Array.isArray(v) ? (v as number[]) : [];
    };

    /** 弹窗内选项列表（与页面上原多选数据源一致） */
    private getTagPickerOptions = (): MultiSelectTagOption[] => {
        const { tagPickerKind, menusList, printGroup, firstMenus } = this.state;
        if (tagPickerKind === 'ignoreMenu') {
            return menusList.map((temp) => ({
                value: Number(temp.dmi_slu_id),
                label: temp.first_menu_name
                    ? `${temp.dmi_slu_name} - ${temp.first_menu_name}`
                    : String(temp.dmi_slu_name),
            }));
        }
        if (tagPickerKind === 'ignorePosition') {
            return printGroup.map((temp: { print_class_id: number; print_class_name: string }) => ({
                value: Number(temp.print_class_id),
                label: String(temp.print_class_name),
            }));
        }
        if (tagPickerKind === 'mopMenu') {
            return firstMenus.map((temp) => ({
                value: Number(temp.main_group_id),
                label: temp.main_group_name || '',
            }));
        }
        return [];
    };

    /** 弹窗保存按钮与接口 `persistingKeys` 同步展示 loading */
    private getTagDialogSaveLoading = (): boolean => {
        if (!this.state.tagPickerKind) {
            return false;
        }
        if (this.state.tagPickerKind === 'mopMenu') {
            return Boolean(this.state.persistingKeys[1]);
        }
        return Boolean(this.state.persistingKeys[111]);
    };

    /** 弹窗确定：写入表单并请求保存，成功后再关窗 */
    private handleTagPickerSave = async () => {
        const { tagPickerKind, tagPickerDraftIds } = this.state;
        if (!tagPickerKind) {
            return;
        }
        const uiKey: number = tagPickerKind === 'mopMenu' ? 1 : tagPickerKind === 'ignorePosition' ? -111.1 : -111;
        const newVal: unknown = uiKey === 1 ? tagPickerDraftIds : JSON.stringify(tagPickerDraftIds);
        if (!this.onChangeSetting(newVal, uiKey)) {
            return;
        }
        await new Promise<void>((r) => this.setState({}, r));
        const ok = await this.persistSettingByUiKey(uiKey);
        if (ok) {
            this.closeTagPicker();
        }
    };

    /**
     * 行内已选标签删除：与稿面 tag 上「×」一致，改表单并立即落库。
     * @param uiKey - `-111` | `-111.1` | `1`
     * @param id - 要移除的选项 id
     */
    private removeIdFromMultiselectField = (uiKey: number, id: number) => {
        const item = this.state.settingArr.find((x) => x.key === uiKey);
        if (!item) {
            return;
        }
        if (uiKey === 1) {
            const arr = Array.isArray(item.value) ? (item.value as number[]) : [];
            const newVal = arr.filter((x) => x !== id);
            if (this.onChangeSetting(newVal, uiKey)) {
                this.setState({}, () => {
                    void this.persistSettingByUiKey(uiKey);
                });
            }
        } else {
            const ids = JSON.parse(String(item.value ?? '[]')) as number[];
            const newVal = JSON.stringify(ids.filter((x) => x !== id));
            if (this.onChangeSetting(newVal, uiKey)) {
                this.setState({}, () => {
                    void this.persistSettingByUiKey(uiKey);
                });
            }
        }
    };

    /**
     * 辅助模式下在设置项下方展示设计稿中的说明（与 Figma 1838:51547 次要文本一致）。
     * @param settingKey - {@link BasicSettingType.key}
     */
    private renderSelfOrderAuxiliaryHint(settingKey: number): React.ReactNode {
        if (!this.context?.auxiliaryModeEnabled) {
            return null;
        }
        const msgKey = SELF_ORDER_AUX_HINT_I18N_KEYS[settingKey];
        if (!msgKey) {
            return null;
        }
        return (
            <Typography
                variant="body2"
                component="div"
                className="self-order-common-setting-page__aux-hint"
                color="text.secondary"
                sx={{ mt: 0.5, fontWeight: 500, lineHeight: '24px' }}
            >
                {i18n.t(msgKey)}
            </Typography>
        );
    }

    // *********************
    // View
    // *********************

    render() {
        if (!this.state.initialPageDataReady) {
            return <SelfOrderCommonSettingPageSkeleton />;
        }
        const { settingArr, menusList, ignoreOrder, isAlertOpen, backupSettingArr, printGroup } = this.state;
        const backTurnSetting = backupSettingArr.filter(item => [-2, -3, 9].includes(item.key));
        const turnSetting = settingArr.filter(item => [-2, -3, 9].includes(item.key));
        const disabledTurn = JSON.stringify(turnSetting) !== JSON.stringify(backTurnSetting);
        /** 按 key 渲染单条配置（三列共用，顺序由列常量控制） */
        const renderSettingItem = (key: number): React.ReactNode => {
            const item = settingArr.find((x) => x.key === key);
            if (!item) {
                return null;
            }
            if (item.key === -111 && !ignoreOrder.enable) {
                return null;
            }
            /* 与 -111 合并在一格内渲染，此处不再单独占一行 */
            if (item.key === -111.1) {
                return null;
            }
            if (item.type === 'switch') {
                const switchLoadingKey = this.resolvePersistLoadingKey(item.key);
                return (
                    <Grid item xs={12} key={key} className="self-order-common-setting-page__setting-item">
                        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="body2" component="span" sx={{ fontWeight: 700, display: 'block' }}>
                                    {item.label}
                                </Typography>
                                {this.renderSelfOrderAuxiliaryHint(item.key)}
                            </Box>
                            <SettingPersistSwitch
                                checked={Boolean(item.value)}
                                loading={Boolean(this.state.persistingKeys[switchLoadingKey])}
                                aria-label={item.label}
                                onChange={(_, checked) => {
                                    if (this.onChangeSetting(checked, item.key)) {
                                        this.schedulePersistAfterChange(item.key);
                                    }
                                }}
                            />
                        </Stack>
                    </Grid>
                );
            }
            if (item.key === -111 && ignoreOrder.enable) {
                const itemPos = settingArr.find((x) => x.key === -111.1);
                if (!itemPos) {
                    return null;
                }
                const menuSource = menusList.map((temp) => ({
                    value: Number(temp.dmi_slu_id),
                    label: temp.first_menu_name
                        ? `${temp.dmi_slu_name} - ${temp.first_menu_name}`
                        : String(temp.dmi_slu_name),
                }));
                const pgSource = printGroup.map((temp: { print_class_id: number; print_class_name: string }) => ({
                    value: Number(temp.print_class_id),
                    label: String(temp.print_class_name),
                }));
                const selectedMenuIds = JSON.parse(String(item.value || '[]')) as number[];
                const selectedPosIds = JSON.parse(String(itemPos.value || '[]')) as number[];
                const labelMenuById = new Map(menuSource.map((m) => [m.value, m.label] as const));
                const labelPosById = new Map(pgSource.map((m) => [m.value, m.label] as const));
                const rowBusy = Boolean(this.state.persistingKeys[111]);
                return (
                    <Grid
                        item
                        xs={12}
                        key={key}
                        className="self-order-common-setting-page__setting-item self-order-common-setting-page__setting-item--ignore-nested-group"
                    >
                        <Typography
                            variant="body2"
                            component="div"
                            className="self-order-common-setting-page__tag-block-title"
                        >
                            {i18n.t('self_order_setting_page_ignore_menus')}
                        </Typography>
                        {this.renderSelfOrderAuxiliaryHint(-111)}
                        <Box className="self-order-common-setting-page__tag-block">
                            <Box className="self-order-common-setting-page__tag-block-inner">
                                {selectedMenuIds.map((id) => (
                                    <Chip
                                        key={id}
                                        className="self-order-common-setting-page__menu-tag-chip"
                                        label={labelMenuById.get(id) ?? String(id)}
                                        variant="outlined"
                                        disabled={rowBusy}
                                        onDelete={rowBusy ? undefined : () => {
                                            this.removeIdFromMultiselectField(-111, id);
                                        }}
                                        deleteIcon={(
                                            <MingcuteCloseFillIcon
                                                className="self-order-common-setting-page__menu-tag-chip__close"
                                                aria-hidden
                                                focusable="false"
                                            />
                                        )}
                                    />
                                ))}
                                <Button
                                    type="button"
                                    variant="outlined"
                                    className="self-order-common-setting-page__menu-tag-add"
                                    endIcon={(
                                        <IcRoundPlusIcon
                                            className="self-order-common-setting-page__menu-tag-add__plus"
                                            aria-hidden
                                            focusable="false"
                                        />
                                    )}
                                    disabled={rowBusy}
                                    onClick={() => {
                                        this.openTagPicker('ignoreMenu');
                                    }}
                                >
                                    {i18n.t('business_hour_compound_add_menu_plus')}
                                </Button>
                            </Box>
                        </Box>
                        <div className="self-order-common-setting-page__ignore-nested-gap">
                            <Typography
                                variant="body2"
                                component="div"
                                className="self-order-common-setting-page__tag-block-title"
                            >
                                {i18n.t('self_order_setting_page_ignore_print_class')}
                            </Typography>
                            {this.renderSelfOrderAuxiliaryHint(-111.1)}
                            <Box className="self-order-common-setting-page__tag-block">
                                <Box className="self-order-common-setting-page__tag-block-inner">
                                    {selectedPosIds.map((id) => (
                                        <Chip
                                            key={id}
                                            className="self-order-common-setting-page__menu-tag-chip"
                                            label={labelPosById.get(id) ?? String(id)}
                                            variant="outlined"
                                            disabled={rowBusy}
                                            onDelete={rowBusy ? undefined : () => {
                                                this.removeIdFromMultiselectField(-111.1, id);
                                            }}
                                            deleteIcon={(
                                                <MingcuteCloseFillIcon
                                                    className="self-order-common-setting-page__menu-tag-chip__close"
                                                    aria-hidden
                                                    focusable="false"
                                                />
                                            )}
                                        />
                                    ))}
                                    <Button
                                        type="button"
                                        variant="outlined"
                                        className="self-order-common-setting-page__menu-tag-add"
                                        endIcon={(
                                            <IcRoundPlusIcon
                                                className="self-order-common-setting-page__menu-tag-add__plus"
                                                aria-hidden
                                                focusable="false"
                                            />
                                        )}
                                        disabled={rowBusy}
                                        onClick={() => {
                                            this.openTagPicker('ignorePosition');
                                        }}
                                    >
                                        {i18n.t('self_order_setting_page_add_position')}
                                    </Button>
                                </Box>
                            </Box>
                        </div>
                    </Grid>
                );
            }
            if (item.type === 'radio') {
                return (
                    <Grid item xs={12} key={key} className="self-order-common-setting-page__setting-item">
                        <FormControl component="fieldset" fullWidth disabled={Boolean(this.state.persistingKeys[item.key])}>
                            <Typography variant="body2" sx={{ mb: 1, fontWeight: 700 }}>
                                {item.label}
                            </Typography>
                            <RadioGroup
                                row
                                name={`self-order-radio-${item.key}`}
                                value={item.value === null || item.value === undefined ? '' : String(item.value)}
                                onChange={(_, val) => {
                                    const first = (item.radioItems || [])[0];
                                    const t = typeof first?.value;
                                    const coerced =
                                        t === 'number' ? Number(val) : t === 'string' ? String(val) : val;
                                    if (this.onChangeSetting(coerced, item.key)) {
                                        this.schedulePersistAfterChange(item.key);
                                    }
                                }}
                            >
                                {(item.radioItems || []).map((radioItem) => (
                                    <FormControlLabel
                                        key={String(radioItem.value)}
                                        value={String(radioItem.value)}
                                        control={<Radio color="primary" />}
                                        label={radioItem.label}
                                    />
                                ))}
                            </RadioGroup>
                            {this.renderSelfOrderAuxiliaryHint(item.key)}
                        </FormControl>
                    </Grid>
                );
            }
            if (item.type === 'input') {
                /** 轮次三项 + 结束营业前通知时间（Figma）：左标题与辅助说明，右固定宽约 120px 数字输入 */
                const isLabelLeftInputRightRow =
                    item.key === -2 || item.key === -3 || item.key === 9 || item.key === 29;
                if (isLabelLeftInputRightRow) {
                    return (
                        <Grid item xs={12} key={key} className="self-order-common-setting-page__setting-item">
                            <Stack
                                direction="row"
                                alignItems="flex-start"
                                justifyContent="space-between"
                                spacing={2}
                                className="self-order-common-setting-page__round-metric-row"
                            >
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 700, display: 'block' }}>
                                        {item.label}
                                    </Typography>
                                    {this.renderSelfOrderAuxiliaryHint(item.key)}
                                    {item.key === -2 ? (
                                        <Button
                                            type="button"
                                            variant="outlined"
                                            size="small"
                                            startIcon={<LinkIcon />}
                                            disabled={disabledTurn}
                                            onClick={() => this.setAlertOpen(true)}
                                            sx={{ mt: 1 }}
                                        >
                                            {i18n.t('self_order_setting_page_async_turn_setting_to_business_time')}
                                        </Button>
                                    ) : null}
                                </Box>
                                <Box
                                    className="self-order-common-setting-page__round-metric-input-wrap"
                                    sx={{
                                        flexShrink: 0,
                                        width: 120,
                                        maxWidth: '40%',
                                        /* -2 左侧含同步按钮，输入顶对齐；其余两项与左侧标题区垂直居中 */
                                        alignSelf: item.key === -2 ? 'flex-start' : 'center',
                                    }}
                                >
                                    <CommonInput
                                        inputType="INPUT"
                                        fullWidth
                                        disabled={Boolean(this.state.persistingKeys[item.key])}
                                        type={item.inputType === 'number' ? 'number' : 'text'}
                                        value={item.value as string | number}
                                        inputProps={{
                                            onBlur: () => {
                                                this.persistInputFieldIfDirty(item.key);
                                            },
                                        }}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                            if (item.inputType === 'number') {
                                                const v = Number(e.target.value);
                                                if (v >= 0) {
                                                    this.onChangeSetting(v, item.key);
                                                }
                                            } else {
                                                this.onChangeSetting(e.target.value, item.key);
                                            }
                                        }}
                                    />
                                </Box>
                            </Stack>
                        </Grid>
                    );
                }
                /** 其余 input 仍为标题在上、输入在下 */
                return (
                    <Grid item xs={12} key={key} className="self-order-common-setting-page__setting-item">
                        <Box sx={{ mb: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 700, display: 'block' }}>
                                {item.label}
                            </Typography>
                            {this.renderSelfOrderAuxiliaryHint(item.key)}
                        </Box>
                        <CommonInput
                            inputType="INPUT"
                            fullWidth
                            disabled={Boolean(this.state.persistingKeys[item.key])}
                            type={item.inputType === 'number' ? 'number' : 'text'}
                            value={item.value as string | number}
                            inputProps={{
                                onBlur: () => {
                                    this.persistInputFieldIfDirty(item.key);
                                },
                            }}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                if (item.inputType === 'number') {
                                    const v = Number(e.target.value);
                                    if (v >= 0) {
                                        this.onChangeSetting(v, item.key);
                                    }
                                } else {
                                    this.onChangeSetting(e.target.value, item.key);
                                }
                            }}
                        />
                    </Grid>
                );
            }
            if (item.type === 'select') {
                /** 无操作显示广告时间（143）：与轮次/通知时间一致，左标题+说明、右固定宽约 120px 下拉 */
                return (
                    <Grid item xs={12} key={key} className="self-order-common-setting-page__setting-item">
                        <Stack
                            direction="row"
                            alignItems="flex-start"
                            justifyContent="space-between"
                            spacing={2}
                            className="self-order-common-setting-page__round-metric-row"
                        >
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="body2" sx={{ fontWeight: 700, display: 'block' }}>
                                    {item.label}
                                </Typography>
                                {this.renderSelfOrderAuxiliaryHint(item.key)}
                            </Box>
                            <Box
                                className="self-order-common-setting-page__round-metric-input-wrap"
                                sx={{
                                    flexShrink: 0,
                                    width: 120,
                                    maxWidth: '40%',
                                    alignSelf: 'center',
                                }}
                            >
                                <CommonInput
                                    inputType="SELECT"
                                    select
                                    fullWidth
                                    disabled={Boolean(this.state.persistingKeys[item.key])}
                                    label=""
                                    value={item.value as string | number}
                                    items={(item.options || []).map((o) => ({
                                        value: String(o.value),
                                        label: String(o.label),
                                    }))}
                                    onChange={(e) => {
                                        const v = Number(e.target.value);
                                        if (this.onChangeSetting(v, item.key)) {
                                            this.schedulePersistAfterChange(item.key);
                                        }
                                    }}
                                />
                            </Box>
                        </Stack>
                    </Grid>
                );
            }
            if (item.type === 'autoCompleteSelect') {
                const rawOpts = this.getOptions(item);
                const src = rawOpts.map((o) => ({
                    value: Number(o.value),
                    label: String(o.label),
                }));
                const selectedIds = Array.isArray(item.value) ? (item.value as number[]) : [];
                const labelById = new Map(src.map((m) => [m.value, m.label] as const));
                const rowBusy = Boolean(this.state.persistingKeys[1]);
                return (
                    <Grid item xs={12} key={key} className="self-order-common-setting-page__setting-item">
                        <Typography
                            variant="body2"
                            component="div"
                            className="self-order-common-setting-page__field-title"
                        >
                            {item.label}
                        </Typography>
                        {this.renderSelfOrderAuxiliaryHint(item.key)}
                        <Box className="self-order-common-setting-page__tag-block">
                            <Box className="self-order-common-setting-page__tag-block-inner">
                                {selectedIds.map((id) => (
                                    <Chip
                                        key={id}
                                        className="self-order-common-setting-page__menu-tag-chip"
                                        label={labelById.get(id) ?? String(id)}
                                        variant="outlined"
                                        disabled={rowBusy}
                                        onDelete={rowBusy ? undefined : () => {
                                            this.removeIdFromMultiselectField(1, id);
                                        }}
                                        deleteIcon={(
                                            <MingcuteCloseFillIcon
                                                className="self-order-common-setting-page__menu-tag-chip__close"
                                                aria-hidden
                                                focusable="false"
                                            />
                                        )}
                                    />
                                ))}
                                <Button
                                    type="button"
                                    variant="outlined"
                                    className="self-order-common-setting-page__menu-tag-add"
                                    endIcon={(
                                        <IcRoundPlusIcon
                                            className="self-order-common-setting-page__menu-tag-add__plus"
                                            aria-hidden
                                            focusable="false"
                                        />
                                    )}
                                    disabled={rowBusy}
                                    onClick={() => {
                                        this.openTagPicker('mopMenu');
                                    }}
                                >
                                    {i18n.t('business_hour_compound_add_menu_plus')}
                                </Button>
                            </Box>
                        </Box>
                    </Grid>
                );
            }
            return null;
        };
        const sectionTitles = [
            i18n.t('self_order_setting_page_section_ordering_rules'),
            i18n.t('self_order_setting_page_section_menu_display'),
            i18n.t('self_order_setting_page_section_customer_ui'),
        ];
        const columnKeyLists = [SELF_ORDER_COL1_KEYS, SELF_ORDER_COL2_KEYS, SELF_ORDER_COL3_KEYS];
        return (
            <Box className="self-order-common-setting-page">
                {/* 与 Figma 1834:50121 一致：最外层为白卡（填充/辅助色-100），浅灰底由根节点承担 */}
                <Box
                    className="self-order-common-setting-page__surface"
                    sx={{
                        overflow: 'visible',
                        borderRadius: '16px',
                        backgroundColor: 'var(--fill-neutral-100)',
                        boxShadow: '0 0 4px 0 var(--shadow-25)',
                        p: 2,
                    }}
                >
                    <Box className="self-order-common-setting-page__body">
                        <Grid container spacing={3} className="self-order-common-setting-page__columns">
                            {columnKeyLists.map((keys, colIdx) => (
                                <Grid item xs={12} md={4} key={`self-order-col-${colIdx}`}>
                                    <Paper className="self-order-common-setting-page__panel" elevation={0}>
                                    <Typography
                                        variant="subtitle2"
                                        className="self-order-common-setting-page__section-title"
                                    >
                                        {sectionTitles[colIdx]}
                                    </Typography>
                                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                                        {keys.map((key) => (
                                            <Fragment key={key}>{renderSettingItem(key)}</Fragment>
                                        ))}
                                    </Grid>
                                    </Paper>
                                </Grid>
                            ))}
                        </Grid>
                        {isAlertOpen ? (
                            <AlertDialog
                                open={isAlertOpen}
                                title={i18n.t('dis_setting_page_synchronize')}
                                size="sm"
                                confirmLoading={this.state.syncRoundToBusinessHourConfirmLoading}
                                customContent={
                                    <Typography variant="body2">
                                        {i18n.t('self_order_setting_page_sure_async_turn_setting_to_business_time')}
                                    </Typography>
                                }
                                onCancel={() => this.setAlertOpen(false)}
                                onConfirm={this.asyncTurnSetting}
                            />
                        ) : null}
                        {this.state.tagPickerKind ? (
                            <MultiSelectTagDialog
                                open
                                title={
                                    this.state.tagPickerKind === 'ignoreMenu'
                                        ? i18n.t('self_order_setting_page_tag_dialog_title_ignore_menus')
                                        : this.state.tagPickerKind === 'ignorePosition'
                                            ? i18n.t('self_order_setting_page_tag_dialog_title_ignore_positions')
                                            : i18n.t('self_order_setting_page_tag_dialog_title_mop_menus')
                                }
                                selectLabel={
                                    this.state.tagPickerKind === 'ignoreMenu'
                                        ? 'self_order_setting_page_ignore_menus'
                                        : this.state.tagPickerKind === 'ignorePosition'
                                            ? 'self_order_setting_page_ignore_print_class'
                                            : 'self_order_setting_page_mop_qop_menu'
                                }
                                options={this.getTagPickerOptions()}
                                draftIds={this.state.tagPickerDraftIds}
                                onDraftChange={(ids) => {
                                    this.setState({ tagPickerDraftIds: ids });
                                }}
                                onClose={this.closeTagPicker}
                                onSave={this.handleTagPickerSave}
                                saveLoading={this.getTagDialogSaveLoading()}
                            />
                        ) : null}
                    </Box>
                </Box>
            </Box>
        );
    }
}

// *********************
// Type
// *********************

type Props = {

};

type State = {
    settingArr: Array<BasicSettingType>;
    backupSettingArr: Array<BasicSettingType>;
    /** 正在持久化的设置项 key（与 UI 行对应；-111/-111.1 与 111 共用 111） */
    persistingKeys: Partial<Record<number, boolean>>;
    requestQueue: Record<string, {
        field?: string;
        rejected: () => void;
        request: () => Promise<{ id: number; name: string; value: unknown; }[]>;
        patchRequest: (param: any) => Promise<unknown>;
    }>;
    roundRule: RoundRuleParam;
    ignoreOrder: IgnoreOrderParam;
    menusList: (SecondMenu & { first_menu_name?: string })[];
    isAlertOpen: boolean;
    /** 同步轮次至营业时间弹窗：确认中（驱动 AlertDialog 内 LoadingOverlayButton） */
    syncRoundToBusinessHourConfirmLoading: boolean;
    firstMenus: FirstMenu[];
    printGroup: any[];
    /** 多选弹窗类型；与 `tagPickerDraftIds` 成对，为 null 时关窗 */
    tagPickerKind: 'ignoreMenu' | 'ignorePosition' | 'mopMenu' | null;
    /** 弹窗内多选草稿 id */
    tagPickerDraftIds: number[];
    /** 首包数据是否已就绪；为 false 时展示三列骨架（见 `self-order-common-setting-skeleton.component`） */
    initialPageDataReady: boolean;
};

type Request = {
    key: number;
    /** 与 {@link BasicSettingType.value} 一致，随控件类型变化 */
    value: unknown;
    patchRequest: (params: any) => Promise<unknown>;
};

type RoundRuleParam = {
    init: boolean;
    enable: boolean,
    turn_mins: string;
    turn_dishes: string;
}

type IgnoreOrderParam = {
    enable: boolean,
    ignore_menus: string,
    ignore_print_class: string,
}